import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Tabs } from "components/navigation/Tabs"
import { DataTable, SortOrder } from "components/tables/DataTable"

import { fetchGgrByType } from "services/total/total.service"
import { GgrSummaryResponse, GgrSummaryType } from "services/total/types"

import { ChartItem, GgrBarChart } from "./GgrBarChart"
import { countryColumns, GGR_TABS, playerColumns, providerColumns } from "./helpers"

interface GgrSummaryProps {
  timeframe: (Date | null)[]
  tenant: string
}

export const GgrSummarySection: FC<GgrSummaryProps> = ({ timeframe, tenant }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [activeTab, setActiveTab] = useState<GgrSummaryType>(GgrSummaryType.Provider)
  const [loading, setLoading] = useState(false)
  const [activated, setActivated] = useState(false)
  const [data, setData] = useState<Partial<GgrSummaryResponse>>({})
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc")
  const fetchedRef = useRef<Record<string, string>>({})

  const timeframeKey = timeframe.map((d) => d?.toISOString() ?? "").join("_")

  // Reset fetched cache when timeframe/tenant changes
  useEffect(() => {
    fetchedRef.current = {}
    setData({})
    setActivated(false)
  }, [timeframeKey, tenant])

  // Fetch data for the active tab
  useEffect(() => {
    if (!activated) return
    const cacheKey = `${activeTab}_${timeframeKey}_${tenant}_${sortOrder}`
    if (fetchedRef.current[activeTab] === cacheKey) return
    if (!timeframe.some(Boolean)) return

    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await fetchGgrByType(activeTab, timeframe, sortOrder)
        if (!cancelled && res) {
          fetchedRef.current[activeTab] = cacheKey
          setData((prev) => ({ ...prev, ...res }))
        }
      } catch {
        if (!cancelled) toast.error("Failed to load GGR data")
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [activeTab, timeframeKey, tenant, activated, sortOrder])

  const chartItems: ChartItem[] = useMemo(() => {
    switch (activeTab) {
      case GgrSummaryType.Provider:
        return (data.byProvider ?? []).filter((r) => r.provider !== "others").map((r) => ({ name: r.providerTitle, ggr: r.ggr }))
      case GgrSummaryType.Country:
        return (data.byCountry ?? []).filter((r) => r.country !== "Others").map((r) => ({ name: r.country, ggr: r.ggr }))
      case GgrSummaryType.Player:
        return (data.byPlayer ?? []).map((r) => ({ name: r.username || r.displayName || r.userId.slice(0, 8), ggr: r.ggr }))
      default:
        return []
    }
  }, [data, activeTab])

  const tabHasData = useCallback(
    (tab: string) => {
      const key = `${tab}_${timeframeKey}_${tenant}_${sortOrder}`
      return fetchedRef.current[tab] === key
    },
    [timeframeKey, tenant, sortOrder]
  )

  const handleSortGgr = useCallback(() => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"))
  }, [])

  const isTabLoading = loading && !tabHasData(activeTab)

  return (
    <div className="w-full rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="text-lg font-semibold text-white">{t("charts.ggrSummary")}</h3>
        </div>

        <Tabs
          tabs={GGR_TABS.map((tab) => ({ ...tab, label: t(tab.label as string) }))}
          activeTab={activeTab}
          onChange={(key) => setActiveTab(key as GgrSummaryType)}
        />
      </div>

      {!activated ? (
        <div className="flex items-center justify-center py-16">
          <button type="button" onClick={() => setActivated(true)} className="btn btn-primary btn-sm">
            {t("charts.loadGgrSummary")}
          </button>
        </div>
      ) : isTabLoading ? (
        <div className="flex items-center justify-center py-16">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : chartItems.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-gray-500">{t("charts.noDataAvailable")}</div>
      ) : (
        <>
          <GgrBarChart items={chartItems} />

          <div className="mt-4 rounded-lg border border-[rgba(255,255,255,0.06)] overflow-hidden">
            {activeTab === GgrSummaryType.Provider && (
              <DataTable
                rows={data.byProvider ?? []}
                columns={providerColumns}
                rowKey={(r) => r.provider}
                sortKey="ggr"
                sortOrder={sortOrder}
                onSort={handleSortGgr}
              />
            )}
            {activeTab === GgrSummaryType.Country && (
              <DataTable
                rows={data.byCountry ?? []}
                columns={countryColumns}
                rowKey={(r) => r.country}
                sortKey="ggr"
                sortOrder={sortOrder}
                onSort={handleSortGgr}
              />
            )}
            {activeTab === GgrSummaryType.Player && (
              <DataTable
                rows={data.byPlayer ?? []}
                columns={playerColumns}
                rowKey={(r) => r.userId}
                sortKey="ggr"
                sortOrder={sortOrder}
                onSort={handleSortGgr}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}
